// Fraction digits a currency uses (2 for USD/GBP, 0 for zero-decimal JPY/KRW),
// derived from Intl rather than a hardcoded list. Returns 2 for an unknown code.
function currencyDecimals(currency: string, locale: string): number {
  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).resolvedOptions()
        .maximumFractionDigits ?? 2
    )
  } catch {
    return 2
  }
}

/**
 * Format a Stripe minor-unit amount (cents for USD, whole yen for JPY, etc) into
 * a localized currency string. The minor→major divisor is derived from the
 * currency's own decimal count, so zero-decimal currencies (JPY/KRW) are not
 * over-divided by 100. Falls back to a plain "amount CURRENCY" string if
 * Intl.NumberFormat rejects the currency code.
 */
export function formatMinorUnitsAsCurrency(
  amountMinorUnits: number,
  currency: string,
  options: { locale?: string; minimumFractionDigits?: number; maximumFractionDigits?: number } = {}
): string {
  const { locale = 'en-US' } = options
  const decimals = currencyDecimals(currency, locale)
  const major = amountMinorUnits / 10 ** decimals
  const { minimumFractionDigits = decimals, maximumFractionDigits = decimals } = options
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(major)
  } catch {
    return `${major.toFixed(maximumFractionDigits)} ${currency.toUpperCase()}`
  }
}
